import React, { useState, useEffect } from 'react';
import { 
  Shield,
  Ghost,
  Calculator,
  ArrowRight,
  CheckCircle,
  HelpCircle,
  LayoutGrid,
  Zap,
  Globe,
  Lock,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';
import logo from '../logo2.png';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-indigo-50">
      
      {/* Navigation */}
      <nav className={`fixed top-0 w-full px-6 py-6 z-50 transition-all duration-300 ${scrollY > 20 || isMenuOpen ? 'bg-white border-b border-slate-100 shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
             <img src={logo} alt="CUConnect" className="w-10 h-10 object-contain" />
             <span className="font-bold text-xl tracking-tight">CUConnect</span>
          </div>

          <div className="hidden md:flex items-center gap-10 text-sm font-medium text-slate-500">
             <button onClick={() => navigate('/about')} className="hover:text-slate-900 transition-colors">About</button>
             <button onClick={() => navigate('/login')} className="hover:text-slate-900 transition-colors">Calculator</button>
             <button onClick={() => navigate('/login')} className="hover:text-slate-900 transition-colors">Login</button>
          </div>

          <div className="hidden md:block">
            <button 
              onClick={() => navigate('/login')}
              className="px-6 py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-full hover:bg-slate-800 transition-all flex items-center gap-2 group"
            >
              Get Started
              <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden p-2 text-slate-600"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Dropdown */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-slate-100 p-6 animate-in slide-in-from-top-4 duration-300">
            <div className="flex flex-col gap-6">
              <button onClick={() => { navigate('/about'); setIsMenuOpen(false); }} className="text-left text-lg font-semibold text-slate-600 hover:text-slate-900">About</button>
              <button onClick={() => { navigate('/login'); setIsMenuOpen(false); }} className="text-left text-lg font-semibold text-slate-600 hover:text-slate-900">Calculator</button>
              <button onClick={() => { navigate('/login'); setIsMenuOpen(false); }} className="text-left text-lg font-semibold text-slate-600 hover:text-slate-900">Login</button>
              <button 
                onClick={() => { navigate('/login'); setIsMenuOpen(false); }}
                className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold text-center"
              >
                Get Started
              </button>
            </div>
          </div>
        )}
      </nav>

      <main>
        {/* Simple Hero Section */}
        <section className="pt-40 pb-24 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 mb-8 animate-in fade-in slide-in-from-bottom-2 duration-700">
               <span className="relative flex h-2 w-2">
                 <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                 <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-600"></span>
               </span>
               <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600">ISB • LHR • ATD • SWL • WAH • ATK</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-slate-900 mb-8 leading-[1.1]">
              The social hub for <br />
              <span className="text-slate-400">COMSATS students.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed font-medium">
              A private network to connect with your campus, share news anonymously, and access academic tools like our members-only GPA calculator.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/login')}
                className="px-8 py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10"
              >
                Enter CUConnect
              </button>
              
              <button 
                onClick={() => navigate('/login')}
                className="px-8 py-4 bg-white text-slate-600 border-2 border-slate-100 rounded-xl font-bold hover:border-slate-200 transition-all"
              >
                GPA Calculator
              </button>
            </div>
          </div>
        </section>

        {/* Feature Section */}
        <section id="about" className="py-24 bg-slate-50">
           <div className="max-w-6xl mx-auto px-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                 {[
                    { title: "Anonymous Connections", desc: "Interact with your campus community through verified, anonymous aliases. Share news and ask questions freely.", icon: Ghost },
                    { title: "Academic Tools", desc: "Access the most accurate GPA and CGPA calculators specially designed for COMSATS grading policies.", icon: Calculator },
                    { title: "Verified Network", desc: "A secure space exclusive to students. Every user is verified using their official university email.", icon: Shield }
                 ].map((feature, i) => (
                    <div key={i} className="flex flex-col items-start gap-4">
                       <div className="w-12 h-12 bg-white rounded-xl border border-slate-200 flex items-center justify-center text-slate-900 shadow-sm">
                          <feature.icon size={24} />
                       </div>
                       <h3 className="text-xl font-bold text-slate-900">{feature.title}</h3>
                       <p className="text-slate-500 leading-relaxed font-medium">{feature.desc}</p>
                    </div>
                 ))}
              </div>
           </div>
        </section>

        {/* Call to Action */}
        <section className="py-24">
           <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Built for students, by students.</h2>
              <p className="text-lg text-slate-500 mb-10 font-medium">
                Join your university community today. No passwords, no social media tracking—just your campus.
              </p>
              <button 
                onClick={() => navigate('/login')}
                className="px-10 py-5 bg-slate-900 text-white rounded-2xl font-bold hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/10"
              >
                Enter Portal
              </button>
           </div>
        </section>

        {/* Simple Footer */}
        <footer className="py-12 border-t border-slate-100 px-6">
           <div className="max-w-6xl mx-auto flex flex-col items-center md:flex-row md:justify-between gap-8 md:gap-6">
              <div className="flex flex-col items-center md:items-start gap-4">
                 <div className="flex items-center gap-3">
                    <img src={logo} alt="Logo" className="w-6 h-6 grayscale opacity-50" />
                    <span className="text-sm font-bold text-slate-400 tracking-tight">CUConnect</span>
                 </div>
                 <p className="text-xs text-slate-400 font-medium md:hidden">
                    © {new Date().getFullYear()} CUConnect
                 </p>
              </div>

              <div className="flex items-center gap-8 text-sm font-medium text-slate-400">
                 <button onClick={() => navigate('/login')} className="hover:text-slate-900 transition-colors">Calculator</button>
                 <button onClick={() => navigate('/login')} className="hover:text-slate-900 transition-colors">Login</button>
                 <button onClick={() => navigate('/about')} className="hover:text-slate-900 transition-colors">About</button>
              </div>

              <div className="hidden md:block text-sm font-medium text-slate-400">
                 © {new Date().getFullYear()} CUConnect
              </div>
           </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;